import { NextSeo } from 'next-seo';
import Head from 'next/head';

//screens
import { Home } from '@/screens/Latam/Home';

//data-helpers
import { JsonLd } from '@/datafiles/micro-data';
import { getOrganizationLatam } from '@/datafiles/micro-data/organizationLatam';
import { buildFaqSchema } from '@/datafiles/micro-data/faqPage';
import { getLatamDictionary } from '@/datafiles/languages/latam';
import { buildLatamChrome } from '@/datafiles/nav/latam';
import { OgLocaleAlternates } from '@/components/OgLocaleAlternates';
import { getLocalizedSeo } from '@/datafiles/i18n/seo';
import { getPathFor } from '@/datafiles/i18n/translatedPages';
import { LOCALES } from '@/datafiles/i18n/locales';
import { getOpendockUrl } from '@/datafiles/i18n/opendock';

// One body for every LATAM home route (/en/, /pt-br/, /es-mx/, /es-co/): each
// route file only picks the locale.
export function LatamHomePage({ locale }) {
  const { home } = getLatamDictionary(locale);
  const { nav, footer } = buildLatamChrome(locale);
  const seo = getLocalizedSeo('latam-opendock', locale);

  const contactHref = `${getPathFor('latam-contact', locale, {
    includePlanned: true,
  })}#contact-form`;
  const aiHref = getPathFor('latam-home', locale, { includePlanned: true });
  const opendockHref = `${getOpendockUrl(locale)}?utm_source=loadsmart-website`;

  const hero_content = {
    eyebrow: home.hero.eyebrow,
    title: home.hero.title,
    titleHighlight: home.hero.title_highlight,
    copy1: home.hero.copy_1,
    copy2: home.hero.copy_2,
    ctaLabel: home.hero.cta_label,
    ctaHref: contactHref,
    visual: {
      frameTitle: home.hero.visual.frame_title,
      labels: home.hero.visual.labels,
      events: home.hero.visual.events,
    },
  };

  const lifecycleRail_content = {
    kicker: home.lifecycle_rail.kicker,
    title: home.lifecycle_rail.title,
    lede: home.lifecycle_rail.lede,
    managerLabel: home.lifecycle_rail.manager_label,
    stageStatusLabel: home.lifecycle_rail.stage_status_label,
    stages: home.lifecycle_rail.stages,
  };

  const lifecycleCaps = [
    {
      logo: 'loadsmart',
      text: home.lifecycle_rail.caps.loadsmart_ai,
      href: aiHref,
      className: 'sg qlf-btn-5',
    },
    {
      logo: 'opendock',
      text: home.lifecycle_rail.caps.opendock,
      href: opendockHref,
      className: 'od qlf-btn-6',
    },
  ];

  const platformScale_content = {
    label: home.platform_scale.label,
    stats: home.platform_scale.stats,
    locale: LOCALES[locale].htmlLang,
  };

  const endCta_content = {
    title: home.end_cta.title,
    titleHighlight: home.end_cta.title_highlight,
    copy: home.end_cta.copy,
    ctaLabel: home.end_cta.cta_label,
    ctaHref: contactHref,
  };

  const freightExperts_content = {
    kicker: home.freight_experts.kicker,
    title: home.freight_experts.title,
    titleHighlight: home.freight_experts.title_highlight,
    lede: home.freight_experts.lede,
    copy: home.freight_experts.copy,
  };

  const aiCompounding_content = {
    kicker: home.ai_compounding.kicker,
    title: home.ai_compounding.title,
    titleHighlight: home.ai_compounding.title_highlight,
    lede: home.ai_compounding.lede,
    ctaLabel: home.ai_compounding.cta_label,
    ctaHref: aiHref,
    chart: {
      heading: home.ai_compounding.chart.heading,
      quarters: home.ai_compounding.chart.quarters,
      footerLow: home.ai_compounding.chart.footer_low,
      footerHigh: home.ai_compounding.chart.footer_high,
    },
  };

  const proof_content = {
    kicker: home.proof.kicker,
    title: home.proof.title,
    outcomes: Object.values(home.proof.outcomes),
  };

  const rails_content = {
    kicker: home.rails.kicker,
    title: home.rails.title,
    titleHighlight: home.rails.title_highlight,
    lede: home.rails.lede,
    diagram: {
      ariaLabel: home.rails.diagram.aria_label,
      systemsLabel: home.rails.diagram.systems_label,
      freightWorldLabel: home.rails.diagram.freight_world_label,
      apiLabel: home.rails.diagram.api_label,
      ediLabel: home.rails.diagram.edi_label,
      mcpLabel: home.rails.diagram.mcp_label,
      tmsLabel: home.rails.diagram.tms_label,
      erpWmsLabel: home.rails.diagram.erp_wms_label,
      emailEdiLabel: home.rails.diagram.email_edi_label,
      carriersLabel: home.rails.diagram.carriers_label,
      facilitiesLabel: home.rails.diagram.facilities_label,
    },
  };

  return (
    <>
      <Head>
        <meta name="keywords" content={home.meta_keywords} />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title={home.meta_title}
        description={home.meta_description}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
        openGraph={{ url: seo.canonical, locale: seo.ogLocale }}
      />
      <OgLocaleAlternates locales={seo.ogLocaleAlternates} />
      <JsonLd data={getOrganizationLatam(locale)} />
      <JsonLd data={buildFaqSchema(home.faq.items)} />
      <Home
        hero_content={hero_content}
        lifecycleRail_content={lifecycleRail_content}
        platformScale_content={platformScale_content}
        endCta_content={endCta_content}
        freightExperts_content={freightExperts_content}
        aiCompounding_content={aiCompounding_content}
        proof_content={proof_content}
        rails_content={rails_content}
        faq_content={home.faq}
        lifecycleFixedStage={home.lifecycle_rail.fixed_stage}
        lifecycleCaps={lifecycleCaps}
        lifecycleShowProductTag={home.lifecycle_rail.fixed_stage === 'dock'}
        numberLocale={LOCALES[locale].htmlLang}
        nav={nav}
        footer={footer}
      />
    </>
  );
}
